import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";

const NAV_LINKS = ["Home", "Services", "Pricing", "About", "Contact"];

const SERVICES = [
  {
    icon: "🌐",
    title: "Web Development",
    desc: "Robust websites and web platforms engineered with modern tools and frameworks. Whether it’s a marketing site or a large-scale enterprise system, we build solutions focused on speed, reliability, and scalability.",
  },
  {
    icon: "📱",
    title: "Mobile App Development",
    desc: "Innovative mobile applications designed for both iOS and Android. Our native and cross-platform apps deliver smooth performance, intuitive interfaces, and the flexibility to grow with your business.",
  },
  {
    icon: "☁️",
    title: "Cloud Solutions",
    desc: "Flexible cloud environments that support performance and growth. We assist with infrastructure setup, seamless migration, optimization, and managed hosting to ensure your systems remain secure and available.",
  },
  {
    icon: "🛡️",
    title: "Cybersecurity",
    desc: "Strengthen your digital defenses with proactive security strategies. Our services include vulnerability assessments, compliance support, advanced threat monitoring, and continuous protection for your systems.",
  },
  {
    icon: "📊",
    title: "Digital Marketing",
    desc: "Results-focused marketing campaigns powered by SEO, paid advertising, social media, and strategic content. We help increase visibility, attract qualified audiences, and generate measurable business impact.",
  },
  {
    icon: "🤖",
    title: "AI & Automation",
    desc: "Smart technology solutions that simplify operations and enhance productivity. From AI chatbots to automated workflows and data insights, we help reduce manual tasks and improve efficiency.",
  },
];

const PRICING = [
  {
    plan: "Starter",
    price: "$299",
    period: "/mo",
    color: "#00c8ff",
    features: [
      "5-Page Website",
      "Basic SEO Setup",
      "1 Email Account",
      "Monthly Reports",
      "Email Support",
    ],
    cta: "Get Started",
    popular: false,
  },
  {
    plan: "Business",
    price: "$699",
    period: "/mo",
    color: "#ff6b35",
    features: [
      "15-Page Website",
      "Advanced SEO",
      "10 Email Accounts",
      "Weekly Reports",
      "Priority Support",
      "Social Media Management",
    ],
    cta: "Most Popular",
    popular: true,
  },
  {
    plan: "Enterprise",
    price: "Custom",
    period: "",
    color: "#00e5a0",
    features: [
      "As Per Business Needs Pages",
      "Full SEO Suite",
      "As Per Business Needs  Emails",
      "Daily Reports",
      "24/7 Dedicated Support",
      "Custom Integrations",
      "AI Automation",
    ],
    cta: "Contact Us",
    popular: false,
  },
];

const STATS = [
  { value: "500+", label: "Projects Delivered" },
  { value: "12+", label: "Years Experience" },
  { value: "98%", label: "Client Satisfaction" },
  { value: "50+", label: "Team Members" },
];

const TESTIMONIALS = [
  {
    name: "Sarah Mitchell",
    role: "CEO, NovaTech",
    text: "ingiwebservices transformed our digital presence completely. Their team is professional, creative, and delivers on time every single time.",
    avatar: "SM",
  },
  {
    name: "David Chen",
    role: "Founder, GrowthLab",
    text: "The mobile app they built for us exceeded all expectations. Downloads tripled in the first month after launch.",
    avatar: "DC",
  },
  {
    name: "Priya Sharma",
    role: "CTO, FinEdge",
    text: "Their cloud migration was seamless with zero downtime. Best technical team I've worked with in 15 years.",
    avatar: "PS",
  },
];

export default function IngiWebServices() {
  const [activeNav, setActiveNav] = useState("Home");
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", service: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [animIn, setAnimIn] = useState(false);

  useEffect(() => {
    setTimeout(() => setAnimIn(true), 100);
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setMenuOpen(false);
    setActiveNav(id.charAt(0).toUpperCase() + id.slice(1));
  };

  // FormSubmit endpoint: replace with your target email or your FormSubmit "random string" (recommended)
  const FORMSUBMIT_ENDPOINT = "https://formsubmit.co/ajax/RINGENIOUSSOLUTIONLLC@outlook.com";


  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");
    setSubmitting(true);

    try {
      const payload = new FormData();
      payload.append("name", formData.name);
      payload.append("email", formData.email);
      payload.append("phone", formData.phone);
      payload.append("service", formData.service);
      payload.append("message", formData.message);

      // FormSubmit extras (see https://formsubmit.co/documentation)
      payload.append("_subject", `New message from ingiwebservices (${formData.service || "General"})`);
      payload.append("_template", "table");
      payload.append("_replyto", formData.email);

      // If you prefer to keep FormSubmit reCAPTCHA enabled, remove the next line.
      // Note: reCAPTCHA can introduce extra steps/redirects that don't fit an AJAX flow.
      payload.append("_captcha", "false");

      const res = await fetch(FORMSUBMIT_ENDPOINT, {
        method: "POST",
        headers: { Accept: "application/json" },
        body: payload,
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.success === false) {
        throw new Error(data?.message || "Unable to submit form");
      }

      setSubmitted(true);
      setFormData({ name: "", email: "", phone: "", service: "", message: "" });
      setTimeout(() => setSubmitted(false), 4000);
    } catch (err) {
      setSubmitError(err?.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const styles = {
    root: {
      fontFamily: "'Sora', 'Nunito', sans-serif",
      background: "#06090f",
      color: "#e8edf5",
      overflowX: "hidden",
      minHeight: "100vh",
    },
    
    lia: {
      color: "#8899aa",
      fontSize: "0.9rem",
      cursor: "pointer",
      textDecoration: "none",
    },
    nav: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      zIndex: 1000,
      padding: scrolled ? "12px 40px" : "22px 40px",
      background: scrolled ? "rgba(6,9,15,0.97)" : "transparent",
      backdropFilter: scrolled ? "blur(16px)" : "none",
      borderBottom: scrolled ? "1px solid rgba(0,200,255,0.1)" : "none",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      transition: "all 0.3s ease",
    },
    logo: {
      fontSize: "1.5rem",
      fontWeight: 800,
      letterSpacing: "-0.5px",
      color: "#fff",
      cursor: "pointer",
    },
    logoAccent: { color: "#00c8ff" },
    navLinks: {
      display: "flex",
      gap: "36px",
      listStyle: "none",
      margin: 0,
      padding: 0,
    },
    navLink: (active) => ({
      color: active ? "#00c8ff" : "#a0aec0",
      fontWeight: 600,
      fontSize: "0.9rem",
      cursor: "pointer",
      letterSpacing: "0.3px",
      transition: "color 0.2s",
      borderBottom: active ? "2px solid #00c8ff" : "2px solid transparent",
      paddingBottom: "2px",
    }),
    ctaBtn: {
      background: "linear-gradient(135deg, #00c8ff, #0080ff)",
      color: "#fff",
      border: "none",
      padding: "10px 24px",
      borderRadius: "8px",
      fontWeight: 700,
      fontSize: "0.9rem",
      cursor: "pointer",
      boxShadow: "0 4px 20px rgba(0,200,255,0.35)",
      transition: "transform 0.2s, box-shadow 0.2s",
    },
    // HERO
    hero: {
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexDirection: "column",
      textAlign: "center",
      padding: "120px 24px 80px",
      position: "relative",
      overflow: "hidden",
    },
    heroBg: {
      position: "absolute",
      inset: 0,
      background: "radial-gradient(ellipse 80% 60% at 50% 30%, rgba(0,200,255,0.12) 0%, transparent 70%), radial-gradient(ellipse 60% 50% at 80% 80%, rgba(255,107,53,0.08) 0%, transparent 60%)",
      pointerEvents: "none",
    },
    heroGrid: {
      position: "absolute",
      inset: 0,
      backgroundImage: "linear-gradient(rgba(0,200,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(0,200,255,0.04) 1px, transparent 1px)",
      backgroundSize: "60px 60px",
      pointerEvents: "none",
    },
    heroBadge: {
      display: "inline-flex",
      alignItems: "center",
      gap: "8px",
      background: "rgba(0,200,255,0.1)",
      border: "1px solid rgba(0,200,255,0.25)",
      borderRadius: "50px",
      padding: "6px 18px",
      fontSize: "0.8rem",
      fontWeight: 600,
      color: "#00c8ff",
      marginBottom: "28px",
      opacity: animIn ? 1 : 0,
      transform: animIn ? "translateY(0)" : "translateY(20px)",
      transition: "all 0.7s ease 0.1s",
    },
    heroTitle: {
      fontSize: "clamp(2.5rem, 6vw, 5rem)",
      fontWeight: 900,
      lineHeight: 1.1,
      margin: "0 0 24px",
      maxWidth: "900px",
      opacity: animIn ? 1 : 0,
      transform: animIn ? "translateY(0)" : "translateY(30px)",
      transition: "all 0.8s ease 0.2s",
    },
    heroTitleAccent: {
      background: "linear-gradient(135deg, #00c8ff, #0080ff)",
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
    },
    heroSub: {
      fontSize: "1.15rem",
      color: "#8899aa",
      maxWidth: "620px",
      lineHeight: 1.7,
      marginBottom: "44px",
      opacity: animIn ? 1 : 0,
      transform: animIn ? "translateY(0)" : "translateY(20px)",
      transition: "all 0.8s ease 0.35s",
    },
    heroBtns: {
      display: "flex",
      gap: "16px",
      flexWrap: "wrap",
      justifyContent: "center",
      opacity: animIn ? 1 : 0,
      transform: animIn ? "translateY(0)" : "translateY(20px)",
      transition: "all 0.8s ease 0.5s",
    },
    btnPrimary: {
      background: "linear-gradient(135deg, #00c8ff, #0080ff)",
      color: "#fff",
      border: "none",
      padding: "16px 36px",
      borderRadius: "10px",
      fontWeight: 700,
      fontSize: "1rem",
      cursor: "pointer",
      boxShadow: "0 8px 30px rgba(0,200,255,0.4)",
      letterSpacing: "0.3px",
    },
    btnSecondary: {
      background: "transparent",
      color: "#e8edf5",
      border: "2px solid rgba(255,255,255,0.2)",
      padding: "16px 36px",
      borderRadius: "10px",
      fontWeight: 700,
      fontSize: "1rem",
      cursor: "pointer",
      letterSpacing: "0.3px",
    },
    // SECTIONS
    section: {
      padding: "100px 24px",
      maxWidth: "1200px",
      margin: "0 auto",
    },
    sectionLabel: {
      display: "inline-block",
      color: "#00c8ff",
      fontWeight: 700,
      fontSize: "0.8rem",
      letterSpacing: "2px",
      textTransform: "uppercase",
      marginBottom: "14px",
    },
    sectionTitle: {
      fontSize: "clamp(1.8rem, 3.5vw, 2.8rem)",
      fontWeight: 900,
      lineHeight: 1.2,
      margin: "0 0 16px",
    },
    sectionSub: {
      color: "#8899aa",
      fontSize: "1.05rem",
      lineHeight: 1.7,
      maxWidth: "580px",
      marginBottom: "60px",
    },
    // STATS
    statsBar: {
      background: "linear-gradient(135deg, rgba(0,200,255,0.05), rgba(255,107,53,0.05))",
      border: "1px solid rgba(255,255,255,0.06)",
      borderRadius: "20px",
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
      gap: "1px",
      overflow: "hidden",
      margin: "0 24px",
    },
    statItem: {
      padding: "40px 24px",
      textAlign: "center",
      background: "rgba(6,9,15,0.5)",
    },
    statVal: {
      fontSize: "2.5rem",
      fontWeight: 900,
      background: "linear-gradient(135deg, #00c8ff, #ff6b35)",
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
      display: "block",
    },
    statLabel: {
      color: "#8899aa",
      fontSize: "0.9rem",
      marginTop: "6px",
      fontWeight: 500,
    },
    // SERVICES GRID
    servicesGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
      gap: "24px",
    },
    serviceCard: (i) => ({
      background: "rgba(255,255,255,0.03)",
      border: "1px solid rgba(255,255,255,0.07)",
      borderRadius: "16px",
      padding: "36px 32px",
      transition: "transform 0.3s, border-color 0.3s, box-shadow 0.3s",
      cursor: "default",
      position: "relative",
      overflow: "hidden",
    }),
    serviceIcon: {
      fontSize: "2.4rem",
      marginBottom: "20px",
      display: "block",
    },
    serviceTitle: {
      fontSize: "1.2rem",
      fontWeight: 800,
      marginBottom: "12px",
    },
    serviceDesc: {
      color: "#8899aa",
      lineHeight: 1.7,
      fontSize: "0.95rem",
    },
    // PRICING
    pricingGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
      gap: "24px",
      alignItems: "start",
    },
    pricingCard: (popular) => ({
      background: popular ? "linear-gradient(145deg, rgba(255,107,53,0.12), rgba(255,107,53,0.04))" : "rgba(255,255,255,0.03)",
      border: popular ? "2px solid rgba(255,107,53,0.5)" : "1px solid rgba(255,255,255,0.07)",
      borderRadius: "20px",
      padding: "44px 36px",
      position: "relative",
      transform: popular ? "scale(1.03)" : "scale(1)",
    }),
    popularBadge: {
      position: "absolute",
      top: "-14px",
      left: "50%",
      transform: "translateX(-50%)",
      background: "linear-gradient(135deg, #ff6b35, #ff9500)",
      color: "#fff",
      padding: "4px 20px",
      borderRadius: "50px",
      fontSize: "0.75rem",
      fontWeight: 800,
      letterSpacing: "1px",
      whiteSpace: "nowrap",
    },
    planName: {
      fontSize: "0.85rem",
      fontWeight: 700,
      letterSpacing: "2px",
      textTransform: "uppercase",
      color: "#8899aa",
      marginBottom: "12px",
    },
    planPrice: (color) => ({
      fontSize: "3rem",
      fontWeight: 900,
      color: color,
      lineHeight: 1,
    }),
    planPeriod: {
      fontSize: "1rem",
      color: "#8899aa",
      fontWeight: 400,
    },
    planFeatures: {
      listStyle: "none",
      margin: "28px 0",
      padding: 0,
      display: "flex",
      flexDirection: "column",
      gap: "12px",
    },
    planFeature: {
      display: "flex",
      alignItems: "center",
      gap: "10px",
      color: "#c0cfe0",
      fontSize: "0.95rem",
    },
    checkIcon: (color) => ({
      width: "18px",
      height: "18px",
      borderRadius: "50%",
      background: `${color}22`,
      border: `1.5px solid ${color}`,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      fontSize: "10px",
    }),
    planBtn: (color, popular) => ({
      width: "100%",
      padding: "14px",
      borderRadius: "10px",
      border: popular ? "none" : `2px solid ${color}`,
      background: popular ? `linear-gradient(135deg, #ff6b35, #ff9500)` : "transparent",
      color: popular ? "#fff" : color,
      fontWeight: 700,
      fontSize: "0.95rem",
      cursor: "pointer",
      transition: "all 0.2s",
      letterSpacing: "0.3px",
    }),
    // TESTIMONIALS
    testiGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
      gap: "24px",
    },
    testiCard: {
      background: "rgba(255,255,255,0.03)",
      border: "1px solid rgba(255,255,255,0.07)",
      borderRadius: "16px",
      padding: "36px",
    },
    testiQuote: {
      fontSize: "2rem",
      color: "#00c8ff",
      lineHeight: 1,
      marginBottom: "16px",
    },
    testiText: {
      color: "#a0b0c0",
      lineHeight: 1.75,
      fontSize: "0.95rem",
      marginBottom: "24px",
    },
    testiAuthor: {
      display: "flex",
      alignItems: "center",
      gap: "14px",
    },
    testiAvatar: {
      width: "44px",
      height: "44px",
      borderRadius: "50%",
      background: "linear-gradient(135deg, #00c8ff, #0080ff)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontWeight: 800,
      fontSize: "0.8rem",
      flexShrink: 0,
    },
    testiName: {
      fontWeight: 700,
      fontSize: "0.95rem",
    },
    testiRole: {
      color: "#8899aa",
      fontSize: "0.8rem",
      marginTop: "2px",
    },
    // CONTACT
    contactWrap: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: "60px",
      alignItems: "start",
    },
    contactInfo: {},
    contactDetail: {
      display: "flex",
      alignItems: "flex-start",
      gap: "16px",
      marginBottom: "28px",
    },
    contactIcon: {
      width: "44px",
      height: "44px",
      borderRadius: "10px",
      background: "rgba(0,200,255,0.1)",
      border: "1px solid rgba(0,200,255,0.2)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "1.2rem",
      flexShrink: 0,
    },
    contactLabel: {
      fontWeight: 700,
      marginBottom: "4px",
    },
    contactVal: {
      color: "#8899aa",
      fontSize: "0.9rem",
    },
    form: {
      background: "rgba(255,255,255,0.03)",
      border: "1px solid rgba(255,255,255,0.07)",
      borderRadius: "20px",
      padding: "44px",
    },
    formGrid: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: "20px",
      marginBottom: "20px",
    },
    inputWrap: {
      display: "flex",
      flexDirection: "column",
      gap: "8px",
    },
    label: {
      fontSize: "0.85rem",
      fontWeight: 600,
      color: "#a0b0c0",
      letterSpacing: "0.3px",
    },
    input: {
      background: "rgba(255,255,255,0.05)",
      border: "1px solid rgba(255,255,255,0.1)",
      borderRadius: "10px",
      padding: "13px 16px",
      color: "#e8edf5",
      fontSize: "0.95rem",
      outline: "none",
      transition: "border-color 0.2s",
      fontFamily: "inherit",
    },
    textarea: {
      background: "rgba(255,255,255,0.05)",
      border: "1px solid rgba(255,255,255,0.1)",
      borderRadius: "10px",
      padding: "13px 16px",
      color: "#e8edf5",
      fontSize: "0.95rem",
      outline: "none",
      resize: "vertical",
      minHeight: "130px",
      fontFamily: "inherit",
      width: "100%",
      boxSizing: "border-box",
    },
    submitBtn: {
      width: "100%",
      padding: "16px",
      background: "linear-gradient(135deg, #00c8ff, #0080ff)",
      border: "none",
      borderRadius: "10px",
      color: "#fff",
      fontWeight: 700,
      fontSize: "1rem",
      cursor: "pointer",
      marginTop: "8px",
      boxShadow: "0 8px 24px rgba(0,200,255,0.35)",
      fontFamily: "inherit",
      letterSpacing: "0.3px",
    },
    successMsg: {
      textAlign: "center",
      color: "#00e5a0",
      fontWeight: 700,
      padding: "16px",
      background: "rgba(0,229,160,0.1)",
      borderRadius: "10px",
      border: "1px solid rgba(0,229,160,0.2)",
      marginTop: "16px",
    },
    // FOOTER
    footer: {
      borderTop: "1px solid rgba(255,255,255,0.06)",
      padding: "60px 40px 40px",
      maxWidth: "1200px",
      margin: "0 auto",
    },
    footerGrid: {
      display: "grid",
      gridTemplateColumns: "2fr 1fr 1fr 1fr",
      gap: "48px",
      marginBottom: "48px",
    },
    footerTitle: {
      fontSize: "1rem",
      fontWeight: 800,
      marginBottom: "20px",
      color: "#e8edf5",
    },
    footerLinks: {
      listStyle: "none",
      margin: 0,
      padding: 0,
      display: "flex",
      flexDirection: "column",
      gap: "10px",
    },
    footerLink: {
      color: "#8899aa",
      fontSize: "0.9rem",
      cursor: "pointer",
      transition: "color 0.2s",
    },
    footerDesc: {
      color: "#8899aa",
      fontSize: "0.9rem",
      lineHeight: 1.7,
      marginBottom: "24px",
    },
    footerBottom: {
      borderTop: "1px solid rgba(255,255,255,0.06)",
      paddingTop: "28px",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      flexWrap: "wrap",
      gap: "12px",
    },
    footerCopy: {
      color: "#8899aa",
      fontSize: "0.85rem",
    },
    socials: {
      display: "flex",
      gap: "12px",
    },
    socialBtn: {
      width: "36px",
      height: "36px",
      borderRadius: "8px",
      background: "rgba(255,255,255,0.05)",
      border: "1px solid rgba(255,255,255,0.1)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      fontSize: "0.85rem",
      color: "#a0b0c0",
    },
    hamburger: {
      display: "none",
      flexDirection: "column",
      gap: "5px",
      cursor: "pointer",
      padding: "4px",
    },
    hamburgerLine: {
      width: "24px",
      height: "2px",
      background: "#e8edf5",
      borderRadius: "2px",
    },
    mobileMenu: {
      position: "fixed",
      top: "70px",
      left: 0,
      right: 0,
      background: "rgba(6,9,15,0.98)",
      backdropFilter: "blur(20px)",
      padding: "20px 24px",
      zIndex: 999,
      borderBottom: "1px solid rgba(0,200,255,0.1)",
      display: menuOpen ? "flex" : "none",
      flexDirection: "column",
      gap: "4px",
    },
    mobileNavLink: {
      padding: "14px 16px",
      borderRadius: "8px",
      color: "#a0b0c0",
      fontWeight: 600,
      cursor: "pointer",
      fontSize: "1rem",
    },

  };

  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700;800;900&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        html { scroll-behavior: smooth; }
        body { background: #06090f; }
        ::-webkit-scrollbar { width: 6px; }
        ::-webkit-scrollbar-track { background: #06090f; }
        ::-webkit-scrollbar-thumb { background: #00c8ff44; border-radius: 3px; }
        .service-card:hover { transform: translateY(-6px) !important; border-color: rgba(0,200,255,0.3) !important; box-shadow: 0 20px 60px rgba(0,200,255,0.1) !important; }
        .nav-link:hover { color: #00c8ff !important; }
        .footer-link:hover { color: #00c8ff !important; }
        .cta-btn:hover { transform: translateY(-2px); box-shadow: 0 8px 30px rgba(0,200,255,0.5) !important; }
        .input-field:focus { border-color: rgba(0,200,255,0.5) !important; }
        .plan-btn:hover { opacity: 0.85; transform: translateY(-1px); }
        @media (max-width: 900px) {
          .nav-links { display: none !important; }
          .nav-cta { display: none !important; }
          .hamburger { display: flex !important; }
          .contact-wrap { grid-template-columns: 1fr !important; }
          .footer-grid { grid-template-columns: 1fr 1fr !important; }
          .form-grid { grid-template-columns: 1fr !important; }
        }
        @media (max-width: 600px) {
          .footer-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>

      <div style={styles.root}>
        {/* NAV */}
        <nav style={styles.nav}>
          <div style={styles.logo} onClick={() => scrollTo("home")}>
            ingi<span style={styles.logoAccent}>web</span>services
          </div>
          <ul style={styles.navLinks} className="nav-links">
            {NAV_LINKS.map((l) => (
              <li key={l} style={styles.navLink(activeNav === l)} className="nav-link"
                onClick={() => scrollTo(l.toLowerCase())}>
                {l}
              </li>
            ))}
          </ul>
          <button style={styles.ctaBtn} className="cta-btn nav-cta" onClick={() => scrollTo("contact")}>
            Get a Free Quote
          </button>
          <div style={styles.hamburger} className="hamburger" onClick={() => setMenuOpen(!menuOpen)}>
            <div style={styles.hamburgerLine} />
            <div style={styles.hamburgerLine} />
            <div style={styles.hamburgerLine} />
          </div>
        </nav>

        {/* MOBILE MENU */}
        <div style={styles.mobileMenu}>
          {NAV_LINKS.map((l) => (
            <div key={l} style={styles.mobileNavLink} onClick={() => scrollTo(l.toLowerCase())}>{l}</div>
          ))}
        </div>

        {/* HERO */}
        <section id="home" style={styles.hero}>
          <div style={styles.heroBg} />
          <div style={styles.heroGrid} />
          <div style={styles.heroBadge}>
            <span>⚡</span> Trusted by 500+ businesses worldwide
          </div>
          <h1 style={styles.heroTitle}>
            Powering Your Business<br />
            <span style={styles.heroTitleAccent}>in the Digital Age</span>
          </h1>
          <p style={styles.heroSub}>
            We deliver innovative IT solutions, web development, cloud services, and digital marketing strategies designed to streamline your operations and fuel long-term growth.
          </p>
          <div style={styles.heroBtns}>
            <button style={styles.btnPrimary} onClick={() => scrollTo("services")}>Explore Services</button>
            <button style={styles.btnSecondary} onClick={() => scrollTo("pricing")}>View Pricing</button>
          </div>
        </section>

        {/* STATS */}
        <div style={styles.statsBar}>
          {STATS.map((s) => (
            <div key={s.label} style={styles.statItem}>
              <span style={styles.statVal}>{s.value}</span>
              <span style={styles.statLabel}>{s.label}</span>
            </div>
          ))}
        </div>

        {/* SERVICES */}
        <section id="services">
          <div style={styles.section}>
            <span style={styles.sectionLabel}>What We Do</span>
            <h2 style={styles.sectionTitle}>Comprehensive IT &<br />Digital Services</h2>
            <p style={styles.sectionSub}>
              From strategy to execution, we provide end-to-end technology services tailored to accelerate your business growth.
            </p>
            <div style={styles.servicesGrid}>
              {SERVICES.map((s, i) => (
                <div key={s.title} style={styles.serviceCard(i)} className="service-card">
                  <span style={styles.serviceIcon}>{s.icon}</span>
                  <h3 style={styles.serviceTitle}>{s.title}</h3>
                  <p style={styles.serviceDesc}>{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* PRICING */}
        <section id="pricing" style={{ background: "rgba(255,255,255,0.01)", padding: "50px 0" }}>
          <div style={styles.section}>
            <span style={styles.sectionLabel}>Pricing Plans</span>
            <h2 style={styles.sectionTitle}>Simple, Transparent<br />Pricing</h2>
            <p style={styles.sectionSub}>
              No hidden fees. Choose the plan that fits your business and scale up anytime.
            </p>
            <div style={styles.pricingGrid}>
              {PRICING.map((p) => (
                <div key={p.plan} style={styles.pricingCard(p.popular)}>
                  {p.popular && <div style={styles.popularBadge}>⭐ MOST POPULAR</div>}
                  <div style={styles.planName}>{p.plan}</div>
                  <div>
                    <span style={styles.planPrice(p.color)}>{p.price}</span>
                    <span style={styles.planPeriod}>{p.period}</span>
                  </div>
                  <ul style={styles.planFeatures}>
                    {p.features.map((f) => (
                      <li key={f} style={styles.planFeature}>
                        <div style={styles.checkIcon(p.color)}>✓</div>
                        {f}
                      </li>
                    ))}
                  </ul>
                  <button style={styles.planBtn(p.color, p.popular)} className="plan-btn"
                    onClick={() => scrollTo("contact")}>
                    {p.cta}
                  </button>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* TESTIMONIALS */}
        {/* <section id="about">
          <div style={styles.section}>
            <span style={styles.sectionLabel}>Client Stories</span>
            <h2 style={styles.sectionTitle}>What Our Clients<br />Say About Us</h2>
            <p style={styles.sectionSub}>
              Real results from real businesses. Here's how ingiwebservices has helped companies like yours thrive.
            </p>
            <div style={styles.testiGrid}>
              {TESTIMONIALS.map((t) => (
                <div key={t.name} style={styles.testiCard}>
                  <div style={styles.testiQuote}>"</div>
                  <p style={styles.testiText}>{t.text}</p>
                  <div style={styles.testiAuthor}>
                    <div style={styles.testiAvatar}>{t.avatar}</div>
                    <div>
                      <div style={styles.testiName}>{t.name}</div>
                      <div style={styles.testiRole}>{t.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section> */}

        {/* CONTACT */}
        <section id="contact" style={{ background: "rgba(255,255,255,0.01)", padding: "50px 0" }}>
          <div style={styles.section}>
            <span style={styles.sectionLabel}>Get In Touch</span>
            <h2 style={styles.sectionTitle}>Let's Build Something<br />Amazing Together</h2>
            <div style={{ ...styles.contactWrap }} className="contact-wrap">
              {/* INFO */}
              <div style={styles.contactInfo}>
                <p style={{ color: "#8899aa", lineHeight: 1.8, marginBottom: "44px", fontSize: "1rem" }}>
                  Ready to take your business to the next level? Fill out the form and our team will get back to you within 24 hours with a customized solution.
                </p>
                {[
                  { icon: "📧", label: "Email Us", val: "support@ingiwebservices.com" },
                  { icon: "📞", label: "Call Us", val: "+1 (831) 920-6933" },
                  { icon: "📍", label: "Our Office", val: "5752 Keron Ln, Grovetown GA 30813" },
                  { icon: "⏰", label: "Working Hours", val: "Mon–Fri, 9AM – 6PM PST" },
                ].map((item) => (
                  <div key={item.label} style={styles.contactDetail}>
                    <div style={styles.contactIcon}>{item.icon}</div>
                    <div>
                      <div style={styles.contactLabel}>{item.label}</div>
                      <div style={styles.contactVal}>{item.val}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* FORM */}
              <div style={styles.form}>
                <h3 style={{ fontSize: "1.3rem", fontWeight: 800, marginBottom: "28px" }}>Send Us a Message</h3>
                <form onSubmit={handleSubmit}>
                  {/* FormSubmit honeypot (bots will fill this; humans won't) */}
                  <input type="text" name="_honey" style={{ display: "none" }} tabIndex="-1" autoComplete="off" />

                  <div style={styles.formGrid} className="form-grid">
                    <div style={styles.inputWrap}>
                      <label style={styles.label}>Full Name *</label>
                      <input
                        required
                        name="name"
                        className="input-field"
                        style={styles.input}
                        placeholder="John Doe"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      />
                    </div>

                    <div style={styles.inputWrap}>
                      <label style={styles.label}>Email Address *</label>
                      <input
                        required
                        name="email"
                        type="email"
                        className="input-field"
                        style={styles.input}
                        placeholder="john@company.com"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      />
                    </div>

                    <div style={styles.inputWrap}>
                      <label style={styles.label}>Phone Number</label>
                      <input
                        name="phone"
                        className="input-field"
                        style={styles.input}
                        placeholder="+1 (555) 000-0000"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      />
                    </div>

                    <div style={styles.inputWrap}>
                      <label style={styles.label}>Service Needed</label>
                      <select
                        name="service"
                        className="input-field"
                        style={{ ...styles.input, appearance: "none" }}
                        value={formData.service}
                        onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                      >
                        <option value="" style={{ background: "#06090f" }}>Select a service…</option>
                        {SERVICES.map((s) => (
                          <option key={s.title} value={s.title} style={{ background: "#06090f" }}>
                            {s.title}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div style={{ ...styles.inputWrap, marginBottom: "20px" }}>
                    <label style={styles.label}>Your Message *</label>
                    <textarea
                      required
                      name="message"
                      className="input-field"
                      style={styles.textarea}
                      placeholder="Tell us about your project, goals, and timeline…"
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    />
                  </div>

                  <button type="submit" style={styles.submitBtn} disabled={submitting}>
                    {submitting ? "Sending…" : "🚀 Send Message"}
                  </button>

                  {submitError && (
                    <div
                      style={{
                        ...styles.successMsg,
                        background: "rgba(255, 80, 80, 0.12)",
                        borderColor: "rgba(255, 80, 80, 0.25)",
                      }}
                    >
                      ❌ {submitError}
                    </div>
                  )}

                  {submitted && (
                    <div style={styles.successMsg}>
                      ✅ Message sent! We'll be in touch within 24 hours.
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>

        {/* FOOTER */}
        <footer>
          <div style={styles.footer}>
            <div style={styles.footerGrid} className="footer-grid">
              <div>
                <div style={{ ...styles.logo, fontSize: "1.4rem", marginBottom: "16px" }}>
                  ingi<span style={styles.logoAccent}>web</span>services
                </div>
                <p style={styles.footerDesc}>
                  Empowering businesses with cutting-edge IT solutions, from web development and cloud infrastructure to digital marketing and AI automation.
                </p>
                
              </div>

              <div>
                <div style={styles.footerTitle}></div>
                <ul style={styles.footerLinks}>
                  {[""].map((l) => (
                    <li key={l} style={styles.footerLink} className="footer-link">{l}</li>
                  ))}
                </ul>
              </div>


              <div>
                <div style={styles.footerTitle}>Services</div>
                <ul style={styles.footerLinks}>
                  {SERVICES.slice(0, 5).map((s) => (
                    <li key={s.title} style={styles.footerLink} className="footer-link"
                      onClick={() => scrollTo("services")}>
                      {s.title}
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <div style={styles.footerTitle}>Legal</div>
                <ul style={styles.footerLinks}>
                  {["privacy-policy", "terms-and-conditions", "", "", ""].map((l) => (
                    <li key={l} style={styles.footerLink} className="footer-link">
                      <Link style={styles.lia} to={`/${l}`}>{l}</Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div style={styles.footerBottom}>
              <div style={styles.footerCopy}>© 2025 ingiwebservices. All rights reserved.</div>
              <div style={{ color: "#8899aa", fontSize: "0.85rem" }}>
                Ingiwebservices
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
